import { useEffect, useState, useRef } from 'react';
import { Modal, Form, Col, Row, Input, message } from 'antd';
import { Button } from '../../../design-system';
import { updateZones } from '../../../api/data';
import { useTheme } from '../../../providers/theme';
import { getColors } from '../../utils/getColors';
import { useSelected } from '../../../providers/selectedContext';
import { useAppContext } from '../../../providers/AppContext';
import { UpdateZonesPayload, Project } from '../../../@types/projects/types';
import { AnalyzeType } from '../../../@types/samples/types';

const EditFlowUnitModal = () => {
  const { isDay } = useTheme();
  const [form] = Form.useForm();
  const { configStates, updateConfigStates, upsertProject } = useAppContext();
  const { selectedInfo, setSelectedInfo } = useSelected();
  const analysis_type = selectedInfo.project?.type_of_analyze;
  const zones = selectedInfo.project?.correlation?.zones;
  const colors = getColors(isDay);
  const [loading, setLoading] = useState(false);

  const workerRef = useRef<Worker | null>(null);

  useEffect(() => {
    workerRef.current = new Worker(new URL('../worker/calculation.worker.ts', import.meta.url), {
      type: 'module',
    });

    return () => {
      workerRef.current?.terminate();
    };
  }, []);

  const runRecalculationInWorker = (project: Project, type: AnalyzeType): Promise<Project> => {
    return new Promise((resolve, reject) => {
      const worker = workerRef.current;
      if (!worker) {
        return reject(new Error('Calculation worker is not initialized.'));
      }

      const handleMessage = (event: MessageEvent) => {
        cleanup();
        const { status, data, message: errorMessage } = event.data;
        if (status === 'success') {
          resolve(data);
        } else {
          reject(new Error(errorMessage));
        }
      };

      const handleError = (error: ErrorEvent) => {
        cleanup();
        reject(error);
      };

      const cleanup = () => {
        worker.removeEventListener('message', handleMessage);
        worker.removeEventListener('error', handleError);
      };

      worker.addEventListener('message', handleMessage);
      worker.addEventListener('error', handleError);

      worker.postMessage({ project, type });
    });
  };

  useEffect(() => {
    if (zones) {
      if (analysis_type === AnalyzeType.LIAO) {
        const initialData = zones.reduce((acc, zone, index) => {
          acc[`best_a_${index}`] = zone.zone_values?.liao_values?.best_a;
          acc[`best_b_${index}`] = zone.zone_values?.liao_values?.best_b;
          acc[`pce_${index}`] = zone.zone_values?.liao_values?.pce;
          acc[`true_pc_max_${index}`] = zone.zone_values?.liao_values?.true_pc_max;
          acc[`swir_${index}`] = zone.zone_values?.liao_values?.swir;
          acc[`no_w_${index}`] = zone.zone_values?.liao_values?.no_w;
          acc[`nw_${index}`] = zone.zone_values?.liao_values?.nw;
          acc[`krw_absolute_${index}`] = zone.zone_values?.liao_values?.krw_absolute;
          acc[`sor_w_${index}`] = zone.zone_values?.liao_values?.sor_w;
          acc[`kro_absolute_${index}`] = zone.zone_values?.liao_values?.kro_absolute;
          acc[`no_g_${index}`] = zone.zone_values?.liao_values?.no_g;
          acc[`ng_${index}`] = zone.zone_values?.liao_values?.ng;
          acc[`krg_absolute_${index}`] = zone.zone_values?.liao_values?.krg_absolute;
          acc[`sgc_${index}`] = zone.zone_values?.liao_values?.sgc;
          acc[`sor_${index}`] = zone.zone_values?.liao_values?.sor;
          acc[`sgmax_${index}`] = zone.zone_values?.liao_values?.sgmax;
          acc[`sor_g_${index}`] = zone.zone_values?.liao_values?.sor_g;
          acc[`pc_max_og_${index}`] = zone.zone_values?.liao_values?.pc_max_og;
          return acc;
        }, {} as any);
        form.setFieldsValue(initialData);
      } else {
        const initialData = zones.reduce((acc, zone, index) => {
          acc[`best_a_${index}`] = zone.zone_values?.liao_values?.best_a;
          acc[`best_b_${index}`] = zone.zone_values?.liao_values?.best_b;
          acc[`pce_${index}`] = zone.zone_values?.liao_values?.pce;
          acc[`true_pc_max_${index}`] = zone.zone_values?.liao_values?.true_pc_max;
          acc[`swir_${index}`] = zone.zone_values?.liao_values?.swir;
          acc[`no_w_${index}`] = zone.zone_values?.liao_values?.no_w;
          acc[`nw_${index}`] = zone.zone_values?.liao_values?.nw;
          acc[`krw_absolute_${index}`] = zone.zone_values?.liao_values?.krw_absolute;
          acc[`sor_w_${index}`] = zone.zone_values?.liao_values?.sor_w;
          acc[`kro_absolute_${index}`] = zone.zone_values?.liao_values?.kro_absolute;
          acc[`no_g_${index}`] = zone.zone_values?.lambda_values?.no_g;
          acc[`ng_${index}`] = zone.zone_values?.lambda_values?.ng;
          acc[`krg_absolute_${index}`] = zone.zone_values?.lambda_values?.krg_absolute;
          acc[`sgc_${index}`] = zone.zone_values?.lambda_values?.sgc;
          acc[`sor_${index}`] = zone.zone_values?.lambda_values?.sor;
          acc[`sgmax_${index}`] = zone.zone_values?.lambda_values?.sgmax;
          acc[`sor_g_${index}`] = zone.zone_values?.lambda_values?.sor_g;
          acc[`pc_max_og_${index}`] = zone.zone_values?.lambda_values?.pc_max_og;
          return acc;
        }, {} as any);
        form.setFieldsValue(initialData);
      }
    }
  }, [zones, form, analysis_type]);

  const handleOk = async (values: any) => {
    if (!selectedInfo.project?.uid || !zones || !analysis_type) return;

    const projectUid = selectedInfo.project.uid;
    setLoading(true);

    try {
      const isLiao = analysis_type === AnalyzeType.LIAO;
      const buildZoneValues = (index: number) => ({
        best_a: Number(values[`best_a_${index}`]), best_b: Number(values[`best_b_${index}`]),
        true_pc_max: Number(values[`true_pc_max_${index}`]), pce: Number(values[`pce_${index}`]),
        swir: Number(values[`swir_${index}`]), no_w: Number(values[`no_w_${index}`]),
        nw: Number(values[`nw_${index}`]), krw_absolute: Number(values[`krw_absolute_${index}`]),
        sor_w: Number(values[`sor_w_${index}`]), kro_absolute: Number(values[`kro_absolute_${index}`]),
        no_g: Number(values[`no_g_${index}`]), ng: Number(values[`ng_${index}`]),
        krg_absolute: Number(values[`krg_absolute_${index}`]), sgc: Number(values[`sgc_${index}`]),
        sor: Number(values[`sor_${index}`]), sgmax: Number(values[`sgmax_${index}`]),
        sor_g: Number(values[`sor_g_${index}`]), pc_max_og: Number(values[`pc_max_og_${index}`])
      });

      const payload: UpdateZonesPayload = {
        project_uid: projectUid,
        user: selectedInfo.project.users[0],
        zones: zones.map((zone, index) => ({
          zone_uid: zone.uid,
          zone_values: isLiao ? { liao_values: buildZoneValues(index) } : { lambda_values: buildZoneValues(index) }
        }))
      };

      const updatedProjectFromApi = await updateZones(projectUid, payload);
      message.info('Values saved. Recalculating results...');

      const recalculatedProject = await runRecalculationInWorker(updatedProjectFromApi, analysis_type);

      setSelectedInfo({ project: recalculatedProject });
      upsertProject(recalculatedProject);
      message.success('Values edited and results recalculated successfully!');
      updateConfigStates({ openEditFlowUnits: false });

    } catch (error) {
      message.error('An error occurred during the process.');
      console.error('ERROR WHILE EDITING AND RECALCULATING:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    updateConfigStates({ openEditFlowUnits: false });
  };

  const labels = [
    { label: 'a', key: 'best_a' }, { label: 'b', key: 'best_b' },
    { label: 'Pce', key: 'pce' }, { label: 'Pcmax_ow', key: 'true_pc_max' },
    { label: 'Swir', key: 'swir' }, { label: 'No_w', key: 'no_w' },
    { label: 'Nw', key: 'nw' }, { label: 'Krw', key: 'krw_absolute' },
    { label: 'Sor_w', key: 'sor_w' }, { label: 'Kro', key: 'kro_absolute' },
    { label: 'Nog', key: 'no_g' }, { label: 'Ng', key: 'ng' },
    { label: 'Krg', key: 'krg_absolute' }, { label: 'Sgc', key: 'sgc' },
    { label: 'Sor', key: 'sor' }, { label: 'Sgmax', key: 'sgmax' },
    { label: 'Sor_g', key: 'sor_g' }, { label: 'Pcmax_og', key: 'pc_max_og' }
  ];

  return (
    <>
      <Modal
        className="custom-modal"
        open={configStates.openEditFlowUnits}
        onCancel={handleCancel}
        footer={null}
        style={{ minWidth: 'fit-content' }}
      >
        <Form
          style={{ marginTop: '1rem' }}
          form={form}
          onFinish={handleOk}
          id="edit-zones-form"
        >
          <Row>
            <Col span={zones ? 24 / (zones.length + 1) : 24}></Col>
            {zones?.map((zone, index) => (
              <Col span={zones ? 24 / (zones.length + 1) : 24}>
                <div
                  style={{
                    width: 'fit-content',
                    display: 'flex',
                    justifyContent: 'center'
                  }}
                >
                  <h6>{`RT ${index + 1}`}</h6>
                </div>
              </Col>
            ))}
          </Row>
          {labels.map(label => (
            <Row key={label.key}>
              <Col span={zones ? 24 / (zones.length + 1) : 24}>
                <h6>{label.label}</h6>
              </Col>
              {zones?.map((zone, index) => (
                <Col span={zones ? 24 / (zones.length + 1) : 24}>
                  <Form.Item name={`${label.key}_${index}`}>
                    <Input style={{ width: '75%' }} />
                  </Form.Item>
                </Col>
              ))}
            </Row>
          ))}
          <div
            style={{
              display: 'flex',
              justifyContent: 'end'
            }}
          >
            <div>
              <Button
                variant="secondary"
                style={{ marginRight: '0.5rem' }}
                onClick={handleCancel}
              >
                Cancel
              </Button>
            </div>
            <div>
              <Button
                variant="primary"
                type="submit"
                form="edit-zones-form"
                loading={loading}
              >
                Edit values
              </Button>
            </div>
          </div>
        </Form>
      </Modal>
    </>
  );
};

export default EditFlowUnitModal;
